/**
 * A layout in a window of its own, and how the windows stay one workspace.
 *
 * Owner, 2026-10-28 (docs/plans/desktop-electron.md, *Windows on several
 * monitors*): any layout tab opens in its own window, on any monitor, showing
 * only its own tab. The main window keeps the tab, marked ⧉.
 *
 * ## Each window is its own copy of the app
 *
 * A layout window loads the app itself (`?layout=<id>&main=<id>`), not a portal
 * into the main window's document as the data panel's pop-out is. It has
 * charts, and a chart in a portal hangs off the opener: its frames and its
 * screen scale are the main window's, so on a second monitor at another scale
 * it would be drawn for the wrong one, and minimising the main window would
 * stall it. Its own copy has its own sockets, frames and scale.
 *
 * ## One window saves
 *
 * Two copies each saving the whole workspace would overwrite each other's
 * edits — and overlapping saves are what hung qf-us before (v343). So the
 * **main window is the only writer**: a layout window sends each edit to it as
 * an action, the main window applies and saves it, and sends the workspace back
 * to every window. Messages carry the main window's id, because a second main
 * window (another browser tab, or Ctrl+Shift+N in the desktop app) has layout
 * windows of its own that must not take its edits.
 *
 * Pure apart from reading `location`; the React half is `use-layout-windows.ts`.
 */

import type { UserWorkspace } from "@qufolio/model";

import type { WorkspaceAction } from "./workspace-actions";

/** Window names: the desktop shell remembers pop-outs by name and reopens them. */
export const LAYOUT_WINDOW_PREFIX = "qf-layout:";

export function layoutWindowName(layoutId: string): string {
  return `${LAYOUT_WINDOW_PREFIX}${layoutId}`;
}

/** The layouts among the window names the shell says were open at quit. */
export function layoutsInWindows(names: readonly string[], known: readonly string[]): string[] {
  return names
    .filter((n) => n.startsWith(LAYOUT_WINDOW_PREFIX))
    .map((n) => n.slice(LAYOUT_WINDOW_PREFIX.length))
    .filter((id, i, all) => known.includes(id) && all.indexOf(id) === i);
}

/** The address a layout window loads. */
export function layoutWindowUrl(layoutId: string, mainId: string, base = "/"): string {
  const params = new URLSearchParams({ layout: layoutId, main: mainId });
  return `${base}?${params}`;
}

/**
 * Which window this is: a layout window names its layout and its main window;
 * anything else is a main window.
 */
export type WindowRole = { kind: "main"; mainId: string } | { kind: "layout"; layoutId: string; mainId: string };

export function windowRole(search: string, mintId: () => string): WindowRole {
  const params = new URLSearchParams(search);
  const layoutId = params.get("layout");
  const mainId = params.get("main");
  if (layoutId && mainId) return { kind: "layout", layoutId, mainId };
  return { kind: "main", mainId: mintId() };
}

/** What the windows say to each other, all on one channel and all tagged `main`. */
export type WindowMessage =
  /** Main → every layout window: the workspace as saved, after every change. */
  | { t: "state"; main: string; workspace: UserWorkspace; status: string }
  /** Layout window → main: an edit, applied and saved there. */
  | { t: "action"; main: string; action: WorkspaceAction }
  /** Layout window → main, on load: send the workspace. */
  | { t: "need-state"; main: string }
  /**
   * Layout window → main: it was clicked. The detached data panel follows the
   * layout in the window clicked last (rule 5), and lives in the main window.
   */
  | { t: "focus"; main: string; layoutId: string; chartId: string | null };

export const CHANNEL = "qf-windows";

/** Whether a message is for the windows of this main window. */
export function isFor(message: unknown, mainId: string): message is WindowMessage {
  return (
    typeof message === "object" &&
    message !== null &&
    (message as { main?: unknown }).main === mainId &&
    typeof (message as { t?: unknown }).t === "string"
  );
}
